import { Box, Flex, Grid, Heading, Spinner } from '@chakra-ui/react';
import { productApprovalsApi } from '@texas/api/endpoints/productApprovalsApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { ProductApprovalInfo } from '@texas/components/article/productApproval/ProductApprovalDetails';
import { CompositionGroupView } from '@texas/components/article/variant/compositions/shared/group/CompositionGroupView';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { productApprovalEvents } from '@texas/components/shared/productApproval/events';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  id: number;
  refetchAll: () => void;
}

export function CustomerProductApprovalDetails({ id, refetchAll }: Props) {
  const { t } = useTranslation();
  const { data, loading, error, refetch, set } = useApiResource(
    productApprovalsApi.get,
  );

  useEffect(() => {
    refetch(id);
  }, [id, refetch]);

  useEffect(() => {
    return productApprovalEvents.productApprovalChanged.subscribe((event) => {
      if (event.id === id) refetch(id);
    });
  }, [id, refetch]);

  const onChanged = () => {
    refetchAll();
    refetch(id);
  };

  if (error) return <ErrorDetails error={error} />;
  if (!data) {
    return loading ? (
      <Flex justify="center" py={8}>
        <Spinner />
      </Flex>
    ) : null;
  }

  return (
    <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={6} p={4}>
      <Box minW={0}>
        <Heading size="sm" mb={4}>
          {t('productApproval.details')}
        </Heading>
        <ProductApprovalInfo
          productApproval={data}
          folderId={data.articleFolderId}
          set={set}
          refetchAll={onChanged}
        />
      </Box>
      <Box minW={0}>
        <Heading size="sm" mb={4}>
          {t('composition.composition')}
        </Heading>
        <CompositionGroupView
          componentSettings={{
            compositionArchiveRestore: 'hidden',
            addMaterials: 'hidden',
            materialsMode: 'read-only',
            removeMaterials: 'hidden',
            dimensionMode: 'read-only',
            expander: 'hidden',
            paStates: 'hidden',
          }}
          variantId={0}
          compositionGroupId={data.compositionGroupId}
        />
      </Box>
    </Grid>
  );
}
